"use client";

import { c as _c } from "react/compiler-runtime";
import type { ActivityLogParams } from "@/components/profile-activity/ProfileActivityLogs";
import { QueryKey } from "@/components/react-query-wrapper/ReactQueryWrapper";
import type { ApiIdentity } from "@/generated/models/ApiIdentity";
import type { ApiRepOverview } from "@/generated/models/ApiRepOverview";
import type { ApiRepCategoriesPage } from "@/generated/models/ApiRepCategoriesPage";
import type { ApiCicOverview } from "@/generated/models/ApiCicOverview";
import { commonApiFetch } from "@/services/api/common-api";
import { AuthContext } from "@/components/auth/Auth";
import { useSeizeConnectContext } from "@/components/auth/SeizeConnectContext";
import MobileWrapperDialog from "@/components/mobile-wrapper-dialog/MobileWrapperDialog";
import { RateMatter } from "@/types/enums";
import { useQuery } from "@tanstack/react-query";
import { useParams } from "next/navigation";
import { useContext, useMemo, useState } from "react";
import UserPageIdentityHeader from "../identity/header/UserPageIdentityHeader";
import UserPageIdentityHeaderCICRate from "../identity/header/cic-rate/UserPageIdentityHeaderCICRate";
import UserPageIdentityStatements from "../identity/statements/UserPageIdentityStatements";
import UserPageRateWrapper from "../utils/rate/UserPageRateWrapper";
import UserPageCombinedActivityLog from "./UserPageCombinedActivityLog";
import type { RepDirection } from "./UserPageRep.helpers";
import { getCanEditNic } from "./UserPageRep.helpers";
import UserPageRepHeader from "./header/UserPageRepHeader";
import UserPageRepMobile from "./UserPageRepMobile";
export default function UserPageRep(t0) {
  const $ = _c(101);
  const {
    profile,
    initialActivityLogParams
  } = t0;
  const params = useParams();
  const t1 = params.user as string;
  let t2;
  if ($[0] !== t1) {
    t2 = t1.toLowerCase();
    $[0] = t1;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const user = t2;
  const {
    connectedProfile,
    activeProfileProxy
  } = useContext(AuthContext);
  const {
    address
  } = useSeizeConnectContext();
  const [repDirection, setRepDirection] = useState("received");
  const [isNicRateOpen, setIsNicRateOpen] = useState(false);
  let t3;
  if ($[2] !== activeProfileProxy || $[3] !== address || $[4] !== connectedProfile || $[5] !== profile) {
    t3 = getCanEditNic({
      connectedProfile,
      targetProfile: profile,
      activeProfileProxy,
      address
    });
    $[2] = activeProfileProxy;
    $[3] = address;
    $[4] = connectedProfile;
    $[5] = profile;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  const canEditNic = t3;
  let t4;
  let t5;
  if ($[7] !== user) {
    t4 = [QueryKey.REP_OVERVIEW, {
      handleOrWallet: user,
      direction: "incoming"
    }];
    t5 = async () => await commonApiFetch({
      endpoint: `profiles/${user}/rep/overview`
    });
    $[7] = user;
    $[8] = t4;
    $[9] = t5;
  } else {
    t4 = $[8];
    t5 = $[9];
  }
  const t6 = !!user;
  let t7;
  if ($[10] !== t4 || $[11] !== t5 || $[12] !== t6) {
    t7 = {
      queryKey: t4,
      queryFn: t5,
      enabled: t6
    };
    $[10] = t4;
    $[11] = t5;
    $[12] = t6;
    $[13] = t7;
  } else {
    t7 = $[13];
  }
  const {
    data: repOverview,
    isFetching: isFetchingOverview
  } = useQuery(t7);
  let t8;
  let t9;
  if ($[14] !== user) {
    t8 = [QueryKey.REP_CATEGORIES, {
      handleOrWallet: user,
      direction: "incoming"
    }];
    t9 = async () => await commonApiFetch({
      endpoint: `profiles/${user}/rep/categories`,
      params: {
        page: "1",
        page_size: "100",
        top_contributors_limit: "5"
      }
    });
    $[14] = user;
    $[15] = t8;
    $[16] = t9;
  } else {
    t8 = $[15];
    t9 = $[16];
  }
  const t10 = !!user;
  let t11;
  if ($[17] !== t10 || $[18] !== t8 || $[19] !== t9) {
    t11 = {
      queryKey: t8,
      queryFn: t9,
      enabled: t10
    };
    $[17] = t10;
    $[18] = t8;
    $[19] = t9;
    $[20] = t11;
  } else {
    t11 = $[20];
  }
  const {
    data: repCategories,
    isFetching: isFetchingCategories
  } = useQuery(t11);
  let t12;
  let t13;
  if ($[21] !== user) {
    t12 = [QueryKey.REP_OVERVIEW, {
      handleOrWallet: user,
      direction: "outgoing"
    }];
    t13 = async () => await commonApiFetch({
      endpoint: `profiles/${user}/rep/overview`,
      params: {
        direction: "outgoing"
      }
    });
    $[21] = user;
    $[22] = t12;
    $[23] = t13;
  } else {
    t12 = $[22];
    t13 = $[23];
  }
  const t14 = !!user && repDirection === "given";
  let t15;
  if ($[24] !== t12 || $[25] !== t13 || $[26] !== t14) {
    t15 = {
      queryKey: t12,
      queryFn: t13,
      enabled: t14
    };
    $[24] = t12;
    $[25] = t13;
    $[26] = t14;
    $[27] = t15;
  } else {
    t15 = $[27];
  }
  const {
    data: repOverviewGiven,
    isFetching: isFetchingOverviewGiven
  } = useQuery(t15);
  let t16;
  let t17;
  if ($[28] !== user) {
    t16 = [QueryKey.REP_CATEGORIES, {
      handleOrWallet: user,
      direction: "outgoing"
    }];
    t17 = async () => await commonApiFetch({
      endpoint: `profiles/${user}/rep/categories`,
      params: {
        direction: "outgoing",
        page: "1",
        page_size: "100",
        top_contributors_limit: "5"
      }
    });
    $[28] = user;
    $[29] = t16;
    $[30] = t17;
  } else {
    t16 = $[29];
    t17 = $[30];
  }
  const t18 = !!user && repDirection === "given";
  let t19;
  if ($[31] !== t16 || $[32] !== t17 || $[33] !== t18) {
    t19 = {
      queryKey: t16,
      queryFn: t17,
      enabled: t18
    };
    $[31] = t16;
    $[32] = t17;
    $[33] = t18;
    $[34] = t19;
  } else {
    t19 = $[34];
  }
  const {
    data: repCategoriesGiven,
    isFetching: isFetchingCategoriesGiven
  } = useQuery(t19);
  let t20;
  let t21;
  if ($[35] !== user) {
    t20 = [QueryKey.CIC_OVERVIEW, {
      handleOrWallet: user
    }];
    t21 = async () => await commonApiFetch({
      endpoint: `profiles/${user}/cic/overview`
    });
    $[35] = user;
    $[36] = t20;
    $[37] = t21;
  } else {
    t20 = $[36];
    t21 = $[37];
  }
  const t22 = !!user;
  let t23;
  if ($[38] !== t20 || $[39] !== t21 || $[40] !== t22) {
    t23 = {
      queryKey: t20,
      queryFn: t21,
      enabled: t22
    };
    $[38] = t20;
    $[39] = t21;
    $[40] = t22;
    $[41] = t23;
  } else {
    t23 = $[41];
  }
  const {
    data: cicOverview
  } = useQuery(t23);
  const activeOverview = repDirection === "received" ? repOverview ?? null : repOverviewGiven ?? null;
  let t24;
  if ($[42] !== repCategories?.data || $[43] !== repCategoriesGiven?.data || $[44] !== repDirection) {
    t24 = repDirection === "received" ? repCategories?.data ?? [] : repCategoriesGiven?.data ?? [];
    $[42] = repCategories?.data;
    $[43] = repCategoriesGiven?.data;
    $[44] = repDirection;
    $[45] = t24;
  } else {
    t24 = $[45];
  }
  const activeCategories = t24;
  const activeLoading = repDirection === "received" ? isFetchingOverview || isFetchingCategories : isFetchingOverviewGiven || isFetchingCategoriesGiven;
  const t25 = cicOverview ?? null;
  let t26;
  if ($[46] !== activeCategories || $[47] !== activeLoading || $[48] !== activeOverview || $[49] !== initialActivityLogParams || $[50] !== profile || $[51] !== repDirection || $[52] !== t25) {
    t26 = <div className="lg:tw-hidden"><UserPageRepMobile profile={profile} overview={activeOverview} categories={activeCategories} cicOverview={t25} repDirection={repDirection} onRepDirectionChange={setRepDirection} initialActivityLogParams={initialActivityLogParams} loading={activeLoading} /></div>;
    $[46] = activeCategories;
    $[47] = activeLoading;
    $[48] = activeOverview;
    $[49] = initialActivityLogParams;
    $[50] = profile;
    $[51] = repDirection;
    $[52] = t25;
    $[53] = t26;
  } else {
    t26 = $[53];
  }
  let t27;
  if ($[54] !== activeCategories || $[55] !== activeLoading || $[56] !== activeOverview || $[57] !== profile || $[58] !== repDirection) {
    t27 = <UserPageRepHeader overview={activeOverview} categories={activeCategories} profile={profile} repDirection={repDirection} onRepDirectionChange={setRepDirection} loading={activeLoading} />;
    $[54] = activeCategories;
    $[55] = activeLoading;
    $[56] = activeOverview;
    $[57] = profile;
    $[58] = repDirection;
    $[59] = t27;
  } else {
    t27 = $[59];
  }
  let t28;
  if ($[60] !== initialActivityLogParams) {
    t28 = <div className="tw-mt-6 lg:tw-mt-8"><UserPageCombinedActivityLog initialActivityLogParams={initialActivityLogParams} /></div>;
    $[60] = initialActivityLogParams;
    $[61] = t28;
  } else {
    t28 = $[61];
  }
  let t29;
  if ($[62] !== t27 || $[63] !== t28) {
    t29 = <div className="tw-min-w-0">{t27}{t28}</div>;
    $[62] = t27;
    $[63] = t28;
    $[64] = t29;
  } else {
    t29 = $[64];
  }
  let t30;
  let t31;
  let t32;
  let t33;
  let t34;
  if ($[65] === Symbol.for("react.memo_cache_sentinel")) {
    t30 = <div className="tw-pointer-events-none tw-absolute tw-inset-0 tw-bg-gradient-to-br tw-from-emerald-500/[0.05] tw-via-transparent tw-to-transparent" />;
    t31 = <div className="tw-absolute tw-left-0 tw-right-0 tw-top-0 tw-h-px tw-bg-gradient-to-r tw-from-transparent tw-via-emerald-400/25 tw-to-transparent" />;
    t32 = <div className="tw-absolute tw-bottom-0 tw-left-0 tw-right-0 tw-h-px tw-bg-gradient-to-r tw-from-transparent tw-via-emerald-400/40 tw-to-transparent" />;
    t33 = <div className="tw-absolute tw-bottom-0 tw-left-0 tw-top-0 tw-w-px tw-bg-gradient-to-b tw-from-transparent tw-via-emerald-400/20 tw-to-transparent" />;
    t34 = <div className="tw-absolute tw-bottom-0 tw-right-0 tw-top-0 tw-w-px tw-bg-gradient-to-b tw-from-transparent tw-via-emerald-400/20 tw-to-transparent" />;
    $[65] = t30;
    $[66] = t31;
    $[67] = t32;
    $[68] = t33;
    $[69] = t34;
  } else {
    t30 = $[65];
    t31 = $[66];
    t32 = $[67];
    t33 = $[68];
    t34 = $[69];
  }
  const t35 = cicOverview ?? null;
  let t36;
  if ($[70] !== canEditNic) {
    t36 = canEditNic ? {
      onRateClick: () => setIsNicRateOpen(true)
    } : {};
    $[70] = canEditNic;
    $[71] = t36;
  } else {
    t36 = $[71];
  }
  let t37;
  if ($[72] !== profile || $[73] !== t35 || $[74] !== t36) {
    t37 = <UserPageIdentityHeader profile={profile} cicOverview={t35} {...t36} />;
    $[72] = profile;
    $[73] = t35;
    $[74] = t36;
    $[75] = t37;
  } else {
    t37 = $[75];
  }
  let t38;
  if ($[76] !== profile) {
    t38 = <UserPageIdentityStatements profile={profile} />;
    $[76] = profile;
    $[77] = t38;
  } else {
    t38 = $[77];
  }
  let t39;
  if ($[78] !== t37 || $[79] !== t38) {
    t39 = <div className="tw-min-w-0 tw-self-start"><div className="tw-relative tw-overflow-hidden tw-rounded-2xl tw-border tw-border-solid tw-border-white/[0.08] tw-bg-[#08090b]">{t30}{t31}{t32}{t33}{t34}<div className="tw-relative tw-z-10">{t37}{t38}</div></div></div>;
    $[78] = t37;
    $[79] = t38;
    $[80] = t39;
  } else {
    t39 = $[80];
  }
  let t40;
  if ($[81] !== t29 || $[82] !== t39) {
    t40 = <div className="tw-hidden lg:tw-block"><div className="tw-grid tw-grid-cols-1 tw-gap-x-8 lg:tw-grid-cols-[minmax(0,2fr)_minmax(22rem,1fr)] xl:tw-gap-x-10">{t29}{t39}</div></div>;
    $[81] = t29;
    $[82] = t39;
    $[83] = t40;
  } else {
    t40 = $[83];
  }
  let t41;
  if ($[84] === Symbol.for("react.memo_cache_sentinel")) {
    t41 = () => setIsNicRateOpen(false);
    $[84] = t41;
  } else {
    t41 = $[84];
  }
  let t42;
  if ($[85] === Symbol.for("react.memo_cache_sentinel")) {
    t42 = () => setIsNicRateOpen(false);
    $[85] = t42;
  } else {
    t42 = $[85];
  }
  let t43;
  if ($[86] !== profile) {
    t43 = <UserPageIdentityHeaderCICRate profile={profile} isTooltip={false} onSuccess={t42} />;
    $[86] = profile;
    $[87] = t43;
  } else {
    t43 = $[87];
  }
  let t44;
  if ($[88] !== profile || $[89] !== t43) {
    t44 = <UserPageRateWrapper profile={profile} type={RateMatter.NIC}>{t43}</UserPageRateWrapper>;
    $[88] = profile;
    $[89] = t43;
    $[90] = t44;
  } else {
    t44 = $[90];
  }
  let t45;
  if ($[91] === Symbol.for("react.memo_cache_sentinel")) {
    t45 = <div className="tw-mt-3"><button onClick={() => setIsNicRateOpen(false)} type="button" className="tw-w-full tw-cursor-pointer tw-rounded-lg tw-border tw-border-solid tw-border-iron-700 tw-bg-iron-900 tw-px-4 tw-py-3 tw-text-sm tw-font-semibold tw-text-white tw-transition tw-duration-300 tw-ease-out hover:tw-bg-iron-800">Cancel</button></div>;
    $[91] = t45;
  } else {
    t45 = $[91];
  }
  let t46;
  if ($[92] !== t44) {
    t46 = <div className="tw-px-4 sm:tw-px-6">{t44}{t45}</div>;
    $[92] = t44;
    $[93] = t46;
  } else {
    t46 = $[93];
  }
  let t47;
  if ($[94] !== isNicRateOpen || $[95] !== t46) {
    t47 = <MobileWrapperDialog title="Rate NIC" isOpen={isNicRateOpen} onClose={t41} tabletModal={true} maxWidthClass="md:tw-max-w-md">{t46}</MobileWrapperDialog>;
    $[94] = isNicRateOpen;
    $[95] = t46;
    $[96] = t47;
  } else {
    t47 = $[96];
  }
  let t48;
  if ($[97] !== t26 || $[98] !== t40 || $[99] !== t47) {
    t48 = <div className="tailwind-scope">{t26}{t40}{t47}</div>;
    $[97] = t26;
    $[98] = t40;
    $[99] = t47;
    $[100] = t48;
  } else {
    t48 = $[100];
  }
  return t48;
}